import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { setUnauthorizedHandler, tokenStore } from './api/client';
import { authApi } from './api/endpoints';
import type { User } from './api/types';

interface AuthState {
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<User>;
  logout: () => void;
  refreshMe: () => void;
}

const AuthContext = React.createContext<AuthState | null>(null);

export const ME_KEY = ['me'] as const;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [hasToken, setHasToken] = React.useState(() => tokenStore.get() !== null);

  const me = useQuery({
    queryKey: ME_KEY,
    queryFn: authApi.me,
    enabled: hasToken,
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });

  const logout = React.useCallback(() => {
    tokenStore.clear();
    setHasToken(false);
    qc.clear();
    navigate('/login', { replace: true });
  }, [qc, navigate]);

  React.useEffect(() => {
    setUnauthorizedHandler(() => {
      if (!hasToken) return;
      toast.error('Your session has ended', { description: 'Please sign in again.', id: 'session' });
      logout();
    });
  }, [hasToken, logout]);

  const login = React.useCallback(
    async (username: string, password: string) => {
      const t = await authApi.login(username, password);
      tokenStore.set(t.access_token, t.expires_in);
      const user = await authApi.me();
      qc.setQueryData(ME_KEY, user);
      setHasToken(true);
      return user;
    },
    [qc],
  );

  const value: AuthState = {
    user: hasToken ? (me.data ?? null) : null,
    loading: hasToken && me.isPending,
    login,
    logout,
    refreshMe: () => void qc.invalidateQueries({ queryKey: ME_KEY }),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/** Signed-in user; only call under <RequireAuth>. */
export function useMe(): User {
  const { user } = useAuth();
  if (!user) throw new Error('useMe without a user');
  return user;
}

export function RequireAuth({ children, admin = false }: { children: React.ReactNode; admin?: boolean }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  if (user.must_change_password && location.pathname !== '/account/password') return <Navigate to="/account/password" replace />;
  if (admin && !user.is_admin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function GuestOnly({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageLoader />;
  // After signing in, go back to the page that sent the user to /login.
  if (user) return <Navigate to={(location.state as { from?: string } | null)?.from ?? '/'} replace />;
  return <>{children}</>;
}

export function FullPageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="size-10 rounded-xl border-4 border-primary/20 border-t-primary animate-spin" aria-label="Loading" />
    </div>
  );
}
